import { useEffect, useMemo, useState } from "react";
import { Alert, Image, ScrollView, Text, View } from "react-native";
import {
  CheckCircle2,
  CircleAlert,
  Link2,
  UserRound,
} from "lucide-react-native";
import {
  ActionBanner,
  Dialog,
  DialogActions,
  DialogIcon,
  DialogPrimaryButton,
  Row,
  SoftSecondaryButton,
  StatusBadge,
  TextActionButton,
  colors,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  useRemoveCareTeamMember,
  type CareTeamMember,
} from "@src/packages/care/hooks/useCareTeam";
import {
  useCareConnections,
  useUpdateCareConnection,
} from "@src/packages/patients/hooks/useCareConnections";
import type { CareConnection } from "@src/packages/patients/hooks/useCareConnections";
import { getDevCareConnectionsOverride } from "@src/packages/patients/dev/devCareConnections";
import { EMBER } from "../../../../onboarding/theme";

type Props = {
  visible: boolean;
  cycleId: string | undefined;
  clinicName?: string | null;
  members: CareTeamMember[];
  onClose: () => void;
  onOpenCareTeam: () => void;
};

const CARE_TEAM_ICON =
  require("../../../../assets/images/treatment-schedule.png") as number;

/**
 * Cycle-scoped care-team management. Official assignments and patient-owned
 * care-team records remain distinct: the former uses the clinic assignment
 * API, while the latter associates an existing record through cycleIds.
 */
export default function CycleCareTeamModal({
  visible,
  cycleId,
  clinicName,
  members,
  onClose,
  onOpenCareTeam,
}: Props) {
  const t = useTranslations();
  const removeMember = useRemoveCareTeamMember();
  const updateCareConnection = useUpdateCareConnection();
  const { data: fetchedCareConnections = [] } = useCareConnections();
  const devCareConnectionsOverride = getDevCareConnectionsOverride();
  const careConnections = devCareConnectionsOverride ?? fetchedCareConnections;
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [actionError, setActionError] = useState(false);
  const [recordPickerOpen, setRecordPickerOpen] = useState(false);
  const [recordActionError, setRecordActionError] = useState<
    "attach" | "detach" | null
  >(null);

  useEffect(() => {
    if (!visible) return;
    setRemovedIds([]);
    setActionError(false);
    const cycleTeamView =
      __DEV__ && typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get(
            "dev_cycle_care_team_view",
          )
        : null;
    setRecordPickerOpen(cycleTeamView === "picker");
    setRecordActionError(null);
  }, [visible]);

  const visibleMembers = useMemo(
    () => members.filter((member) => !removedIds.includes(member.id)),
    [members, removedIds],
  );

  const recordedForCycle = useMemo(
    () =>
      cycleId
        ? careConnections.filter((connection) =>
            connection.cycleIds.includes(cycleId),
          )
        : [],
    [careConnections, cycleId],
  );

  const availableRecordedConnections = useMemo(
    () =>
      careConnections.filter(
        (connection) => !cycleId || !connection.cycleIds.includes(cycleId),
      ),
    [careConnections, cycleId],
  );

  const updateCycleAssociation = async (
    connection: CareConnection,
    action: "attach" | "detach",
  ) => {
    if (!cycleId) return;
    setRecordActionError(null);
    try {
      const cycleIds =
        action === "attach"
          ? Array.from(new Set([...connection.cycleIds, cycleId]))
          : connection.cycleIds.filter((id) => id !== cycleId);
      await updateCareConnection.mutateAsync({
        connectionId: connection.id,
        body: { cycleIds },
      });
      if (action === "attach") setRecordPickerOpen(false);
    } catch {
      setRecordActionError(action);
    }
  };

  const handleRemove = (member: CareTeamMember) => {
    if (!cycleId) return;
    Alert.alert(
      t("care.cycle_clinic_team.remove_member_title", {
        params: { name: member.name },
      }),
      t("care.cycle_clinic_team.remove_member_description"),
      [
        {
          text: t("care.cycle_clinic_team.cancel"),
          style: "cancel",
        },
        {
          text: t("care.cycle_clinic_team.remove_member_confirm"),
          style: "destructive",
          onPress: () => {
            setActionError(false);
            void removeMember
              .mutateAsync({
                cycleId,
                healthcareProfessionalId: member.id,
              })
              .then(() => setRemovedIds((current) => [...current, member.id]))
              .catch(() => setActionError(true));
          },
        },
      ],
    );
  };

  const isBusy = removeMember.isPending || updateCareConnection.isPending;

  return (
    <Dialog
      visible={visible}
      onClose={() => {
        if (!isBusy) onClose();
      }}
      layout="centered"
      closeButton="with-background"
      dismissOnBackdropTap={!isBusy}
      contentStyle={{
        paddingTop: 28,
        paddingHorizontal: 24,
        paddingBottom: 24,
      }}
      testID="cycle-care-team-modal"
    >
      <DialogIcon source={CARE_TEAM_ICON} width={50} height={58} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 20,
          lineHeight: 25,
          fontWeight: "600",
          color: EMBER.colors.text,
          textAlign: "center",
          marginTop: 8,
          marginBottom: 6,
        }}
      >
        {t("care.cycle_clinic_team.manage_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          lineHeight: 20,
          color: EMBER.colors.textLight,
          textAlign: "center",
          marginBottom: 18,
        }}
      >
        {recordPickerOpen
          ? t("care.cycle_clinic_team.add_recorded_member_description")
          : t("care.cycle_clinic_team.manage_description")}
      </Text>

      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 10,
          borderRadius: 12,
          padding: 12,
          marginBottom: 16,
          backgroundColor: "rgba(255,255,255,0.48)",
          borderWidth: 1,
          borderColor: "rgba(92,52,32,0.08)",
        }}
      >
        <Link2 size={17} color={EMBER.colors.warm} strokeWidth={1.8} />
        <Text
          className="font-dm-sans"
          style={{
            flex: 1,
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textMid,
          }}
        >
          {t("care.cycle_clinic_team.manage_match_note")}
        </Text>
      </View>

      {recordPickerOpen ? (
        <View
          style={{
            borderRadius: 12,
            padding: 12,
            marginBottom: 14,
            backgroundColor: "rgba(255,255,255,0.48)",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              lineHeight: 19,
              fontWeight: "600",
              color: EMBER.colors.text,
              marginBottom: 10,
            }}
          >
            {t("care.cycle_clinic_team.add_recorded_member_title")}
          </Text>
          {availableRecordedConnections.length > 0 ? (
            <View
              style={{
                overflow: "hidden",
                borderRadius: 10,
                backgroundColor: "rgba(255,255,255,0.48)",
              }}
            >
              {availableRecordedConnections.map((connection, index) => (
                <Row
                  key={connection.id}
                  surface="plain"
                  leading={<Link2 size={20} color={EMBER.colors.warm} />}
                  leadingSize={24}
                  title={connection.name}
                  subtitle={
                    [
                      connection.role,
                      connection.practiceName,
                      connection.location,
                    ]
                      .filter(Boolean)
                      .join(" · ") || t("care.cycle_clinic_team.recorded_badge")
                  }
                  titleRight={
                    <TextActionButton
                      label={t(
                        "care.cycle_clinic_team.add_recorded_member_action",
                      )}
                      onPress={() =>
                        void updateCycleAssociation(connection, "attach")
                      }
                      disabled={isBusy}
                      color={EMBER.colors.warm}
                    />
                  }
                  gap={10}
                  paddingHorizontal={10}
                  paddingVertical={10}
                  borderRadius={0}
                  style={{
                    borderRadius: 0,
                    borderTopWidth: index > 0 ? 1 : 0,
                    borderTopColor: colors.border,
                  }}
                />
              ))}
            </View>
          ) : (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                lineHeight: 18,
                color: EMBER.colors.textLight,
              }}
            >
              {t("care.cycle_clinic_team.no_available_records")}
            </Text>
          )}
          {recordActionError === "attach" ? (
            <View style={{ marginTop: 10 }}>
              <ActionBanner
                variant="important"
                message={t("care.cycle_clinic_team.attach_failed")}
              />
            </View>
          ) : null}
          <SoftSecondaryButton
            label={t("care.cycle_clinic_team.cancel")}
            onPress={() => {
              setRecordPickerOpen(false);
              setRecordActionError(null);
            }}
            disabled={isBusy}
            style={{ marginTop: 10 }}
          />
        </View>
      ) : null}

      {actionError ? (
        <View style={{ marginBottom: 14 }}>
          <ActionBanner
            variant="important"
            message={t("care.cycle_clinic_team.remove_member_failed")}
          />
        </View>
      ) : null}

      <ScrollView
        style={{ maxHeight: 260, marginBottom: 16 }}
        showsVerticalScrollIndicator={false}
      >
        {clinicName ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              paddingHorizontal: 12,
              paddingVertical: 12,
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.48)",
              marginBottom: 8,
            }}
          >
            <CheckCircle2
              size={20}
              color={EMBER.colors.warm}
              strokeWidth={1.8}
            />
            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  color: EMBER.colors.text,
                  fontWeight: "600",
                }}
              >
                {clinicName}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 16,
                  color: EMBER.colors.textLight,
                }}
              >
                {t("care.cycle_clinic_team.cycle_clinic_label")}
              </Text>
            </View>
            <StatusBadge
              label={t("care.cycle_clinic_team.recorded_badge")}
              tone="green"
              icon={CheckCircle2}
            />
          </View>
        ) : null}

        {recordedForCycle.length > 0 ? (
          <View style={{ marginBottom: visibleMembers.length > 0 ? 8 : 0 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 16,
                fontWeight: "600",
                color: EMBER.colors.textMid,
                marginBottom: 7,
                paddingHorizontal: 3,
              }}
            >
              {t("care.cycle_clinic_team.recorded_team_title")}
            </Text>
            <View
              style={{
                overflow: "hidden",
                borderRadius: 12,
                backgroundColor: "rgba(255,255,255,0.48)",
              }}
            >
              {recordedForCycle.map((connection, index) => (
                <Row
                  key={connection.id}
                  surface="plain"
                  leading={<Link2 size={20} color={EMBER.colors.warm} />}
                  leadingSize={24}
                  title={connection.name}
                  subtitle={
                    [
                      connection.role,
                      connection.practiceName,
                      connection.location,
                    ]
                      .filter(Boolean)
                      .join(" · ") || t("care.cycle_clinic_team.recorded_badge")
                  }
                  titleRight={
                    <TextActionButton
                      label={t(
                        "care.cycle_clinic_team.remove_recorded_member_action",
                      )}
                      onPress={() =>
                        void updateCycleAssociation(connection, "detach")
                      }
                      disabled={isBusy}
                      color={colors.coral}
                    />
                  }
                  gap={10}
                  paddingHorizontal={10}
                  paddingVertical={10}
                  borderRadius={0}
                  style={{
                    borderRadius: 0,
                    borderTopWidth: index > 0 ? 1 : 0,
                    borderTopColor: colors.border,
                  }}
                />
              ))}
            </View>
          </View>
        ) : null}

        {recordActionError === "detach" ? (
          <View style={{ marginBottom: 10 }}>
            <ActionBanner
              variant="important"
              message={t("care.cycle_clinic_team.detach_failed")}
            />
          </View>
        ) : null}

        {visibleMembers.length > 0 ? (
          <View
            style={{
              overflow: "hidden",
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.48)",
            }}
          >
            {visibleMembers.map((member, index) => (
              <Row
                key={member.id}
                surface="plain"
                leading={
                  <Image
                    source={CARE_TEAM_ICON}
                    resizeMode="contain"
                    style={{ width: 24, height: 24 }}
                  />
                }
                leadingSize={26}
                title={member.name}
                subtitle={[member.role, member.email, member.phoneNumber]
                  .filter(Boolean)
                  .join(" · ")}
                titleRight={
                  <TextActionButton
                    label={t("care.cycle_clinic_team.remove_member_action")}
                    onPress={() => handleRemove(member)}
                    disabled={removeMember.isPending}
                    color={colors.coral}
                  />
                }
                accessibilityLabel={`${member.name}. ${member.role}`}
                gap={10}
                paddingHorizontal={12}
                paddingVertical={11}
                borderRadius={0}
                style={{
                  borderRadius: 0,
                  borderTopWidth: index > 0 ? 1 : 0,
                  borderTopColor: colors.border,
                }}
              />
            ))}
          </View>
        ) : null}

        {!clinicName &&
        visibleMembers.length === 0 &&
        recordedForCycle.length === 0 ? (
          <View style={{ alignItems: "center", paddingVertical: 18 }}>
            <CircleAlert
              size={22}
              color={EMBER.colors.textFaint}
              strokeWidth={1.7}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                lineHeight: 20,
                color: EMBER.colors.textLight,
                textAlign: "center",
                marginTop: 8,
              }}
            >
              {t("care.cycle_clinic_team.empty_manage")}
            </Text>
          </View>
        ) : null}
      </ScrollView>

      {!recordPickerOpen ? (
        <SoftSecondaryButton
          label={t("care.cycle_clinic_team.add_recorded_member_action")}
          onPress={() => {
            setRecordActionError(null);
            setRecordPickerOpen(true);
          }}
          disabled={isBusy || !cycleId}
          style={{ marginBottom: 10 }}
          leading={<Link2 size={16} color={EMBER.colors.textMid} />}
        />
      ) : null}

      <DialogActions style={{ marginTop: 0 }}>
        <SoftSecondaryButton
          label={t("care.cycle_clinic_team.open_profile_action")}
          onPress={onOpenCareTeam}
          disabled={isBusy}
          flex={1}
          leading={
            <UserRound
              size={16}
              color={EMBER.colors.textMid}
              strokeWidth={1.8}
            />
          }
        />
        <DialogPrimaryButton
          label={t("care.cycle_clinic_team.done")}
          onPress={onClose}
          disabled={isBusy}
          flex={1}
        />
      </DialogActions>
    </Dialog>
  );
}
